import type { ThreadFilterNegations } from "@productlane/zero/queries"
import type { CustomView } from "@productlane/zero/schema"
import { clientSafeThreadFilters } from "@productlane/zero/queries"

import type { NumericFilter } from "@/components/filters"

export const THREAD_FILTER_ARRAY_KEYS = [
	"companyIds",
	"personIds",
	"issueIds",
	"tagIds",
	"projectIds",
	"assigneeIds",
	"origins",
	"portalInstanceIds",
	"emailAddressIds",
	"painLevels",
	"creatorIds",
	"statuses",
	"replyStates",
	"companyTiers",
	"companyStatuses",
	"companyOwnerIds",
	"issueStatuses",
	"slackWorkspaceIds",
	"slackChannelIds",
	"segmentIds",
] as const

interface ThreadFilterLike {
	negations?: ThreadFilterNegations
	companyRevenue?: NumericFilter | null
	companySize?: NumericFilter | null
}

/**
 * Custom views keep their saved filters until the same dimension is filtered
 * live, in which case the live values and their is / is not operator win.
 */
export function mergeThreadFilters<TLive extends ThreadFilterLike>(
	overrideFilters: CustomView["filters"] | undefined,
	liveFilters: TLive,
): TLive {
	if (!overrideFilters) return clientSafeThreadFilters(liveFilters)

	const merged: Record<string, unknown> = { ...overrideFilters }
	const negations: Record<string, boolean> = { ...overrideFilters.negations }

	const liveValues = liveFilters as Record<string, unknown>
	for (const key of THREAD_FILTER_ARRAY_KEYS) {
		const live = liveValues[key] as unknown[] | undefined
		if (!live || live.length === 0) continue
		merged[key] = live
		if (liveFilters.negations?.[key as keyof ThreadFilterNegations]) {
			negations[key] = true
		} else {
			delete negations[key]
		}
	}
	if (liveFilters.companyRevenue) {
		merged.companyRevenue = liveFilters.companyRevenue
	}
	if (liveFilters.companySize) {
		merged.companySize = liveFilters.companySize
	}
	merged.negations = negations

	return clientSafeThreadFilters(merged as unknown as TLive)
}
